import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import './Subscription.css';

const Subscription = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [processing, setProcessing] = useState(false);

  // Map userType to plans key (deliveryPartner -> delivery_partner)
  const getUserPlanKey = (userType) => {
    if (userType === 'deliveryPartner') return 'delivery_partner';
    return userType;
  };

  useEffect(() => {
    fetchPlans();
    fetchCurrentSubscription();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await api.get('/subscriptions/plans');
      const userType = user?.userType;
      const userPlanKey = getUserPlanKey(userType);
      console.log('Fetched plans:', res.data.plans);
      console.log('User type:', userType);
      console.log('User plan key:', userPlanKey);
      setPlans(res.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load subscription plans');
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/subscriptions/current', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSubscription(res.data.subscription);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setLoading(false);
    }
  };

  const handleSubscribe = (planType) => {
    setSelectedPlan(planType);
    setShowPaymentModal(true);
  };

  const processPayment = async () => {
    if (!selectedPlan) return;

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      // Simulate payment ID (in production, integrate with actual payment gateway)
      const paymentId = `PAY-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      await api.post(
        '/api/subscriptions/subscribe',
        {
          planType: selectedPlan,
          paymentId,
          paymentMethod
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('🎉 Subscription activated successfully!');
      setShowPaymentModal(false);
      setSelectedPlan(null);
      fetchCurrentSubscription();
      
      // Refresh page to update UI
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.message || 'Subscription failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await api.post(
        '/api/subscriptions/cancel',
        { reason: 'User requested cancellation' },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(`Subscription cancelled. ${res.data.refundAmount > 0 ? `Refund: ₹${res.data.refundAmount}` : ''}`);
      fetchCurrentSubscription();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Failed to cancel subscription');
    }
  };

  const handleUpgrade = async () => {
    if (!window.confirm('Upgrade to yearly plan? Your remaining monthly balance will be credited.')) {
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const paymentId = `PAY-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const res = await api.post(
        '/api/subscriptions/upgrade',
        {
          newPlanType: 'yearly',
          paymentId,
          paymentMethod: 'online'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(`🎊 Upgraded to yearly! Credit: ₹${res.data.creditApplied}, Amount paid: ₹${res.data.amountPaid}`);
      fetchCurrentSubscription();
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error(error.response?.data?.message || 'Upgrade failed');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="subscription-loading">Loading subscription details...</div>
      </Layout>
    );
  }

  const userType = user?.userType;
  const userPlanKey = getUserPlanKey(userType);
  
  if (!userType) {
    return (
      <Layout>
        <div className="subscription-container">
          <div className="subscription-header">
            <h1>⚠️ User Type Not Found</h1>
            <p>Please log in again to access subscription plans.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const userPlans = plans[userPlanKey] || {};
  const isSubscribed = currentSubscription && currentSubscription.planType !== 'free' && currentSubscription.status === 'active';

  console.log('Rendering with:', { userType, userPlanKey, userPlans, plans, hasMonthly: !!userPlans.monthly, hasYearly: !!userPlans.yearly });

  return (
    <Layout>
    <div className="subscription-container">
      <div className="subscription-header">
        <h1>📊 Subscription Plans</h1>
        <p>Choose the perfect plan for your business needs</p>
      </div>

      {/* Current Subscription Status */}
      {isSubscribed && (
        <div className="current-subscription-card">
          <div className="subscription-badge">
            <span className="badge-icon">⭐</span>
            <span className="badge-text">Active Subscription</span>
          </div>
          <div className="subscription-details">
            <h3>{currentSubscription.planType.toUpperCase()} PLAN</h3>
            <p className="subscription-amount">₹{currentSubscription.amount}</p>
            <p className="subscription-dates">
              Valid until: {new Date(currentSubscription.endDate).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
            <div className="subscription-features-active">
              <h4>Your Active Features:</h4>
              <ul>
                {currentSubscription.features.loanAccess && <li>✅ Loan Access</li>}
                {currentSubscription.features.premiumSupport && <li>✅ Premium Support</li>}
                {currentSubscription.features.priorityListing && <li>✅ Priority Listing</li>}
                {currentSubscription.features.advancedAnalytics && <li>✅ Advanced Analytics</li>}
                {currentSubscription.features.unlimitedProducts && <li>✅ Unlimited Products</li>}
                {currentSubscription.features.reducedCommission && <li>✅ Reduced Commission</li>}
                {currentSubscription.features.freeDeliveries > 0 && (
                  <li>✅ {currentSubscription.features.freeDeliveries} Free Deliveries/month</li>
                )}
                {currentSubscription.features.higherEarnings && <li>✅ 15% Higher Earnings</li>}
              </ul>
            </div>
            <div className="subscription-actions">
              {currentSubscription.planType === 'monthly' && (
                <button onClick={handleUpgrade} className="btn-upgrade" disabled={processing}>
                  ⬆️ Upgrade to Yearly & Save
                </button>
              )}
              <button onClick={handleCancelSubscription} className="btn-cancel">
                Cancel Subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Plans */}
      <div className="pricing-plans">
        {/* Monthly Plan */}
        {userPlans.monthly && (
          <div className={`pricing-card ${currentSubscription?.planType === 'monthly' ? 'current-plan' : ''}`}>
            <div className="plan-header">
              <h2>Monthly Plan</h2>
              <div className="plan-price">
                <span className="currency">₹</span>
                <span className="amount">{userPlans.monthly.price}</span>
                <span className="period">/month</span>
              </div>
            </div>
            <div className="plan-features">
              <h4>Features Included:</h4>
              <ul>
                {userPlans.monthly.features.loanAccess && (
                  <li>
                    <span className="feature-icon">💰</span>
                    <span>Instant Loan Access</span>
                  </li>
                )}
                {userPlans.monthly.features.premiumSupport && (
                  <li>
                    <span className="feature-icon">🎧</span>
                    <span>24/7 Premium Support</span>
                  </li>
                )}
                {userPlans.monthly.features.priorityListing && (
                  <li>
                    <span className="feature-icon">⭐</span>
                    <span>Priority Listing</span>
                  </li>
                )}
                {userPlans.monthly.features.advancedAnalytics && (
                  <li>
                    <span className="feature-icon">📊</span>
                    <span>Advanced Analytics</span>
                  </li>
                )}
                {userPlans.monthly.features.unlimitedProducts && (
                  <li>
                    <span className="feature-icon">📦</span>
                    <span>Unlimited Products</span>
                  </li>
                )}
                {userPlans.monthly.features.reducedCommission && (
                  <li>
                    <span className="feature-icon">💸</span>
                    <span>Reduced Commission (2% vs 5%)</span>
                  </li>
                )}
                {userPlans.monthly.features.freeDeliveries > 0 && (
                  <li>
                    <span className="feature-icon">🚚</span>
                    <span>{userPlans.monthly.features.freeDeliveries} Free Deliveries</span>
                  </li>
                )}
                {userPlans.monthly.features.higherEarnings && (
                  <li>
                    <span className="feature-icon">📈</span>
                    <span>15% Higher Earnings per Delivery</span>
                  </li>
                )}
              </ul>
            </div>
            {currentSubscription?.planType !== 'yearly' && (
              <button
                onClick={() => handleSubscribe('monthly')}
                className="btn-subscribe"
                disabled={currentSubscription?.planType === 'monthly'}
              >
                {currentSubscription?.planType === 'monthly' ? 'Current Plan' : 'Subscribe Now'}
              </button>
            )}
          </div>
        )}

        {/* Yearly Plan */}
        {userPlans.yearly && (
          <div className={`pricing-card featured ${currentSubscription?.planType === 'yearly' ? 'current-plan' : ''}`}>
            <div className="plan-badge">🏆 Best Value</div>
            <div className="plan-header">
              <h2>Yearly Plan</h2>
              <div className="plan-price">
                <span className="currency">₹</span>
                <span className="amount">{userPlans.yearly.price}</span>
                <span className="period">/year</span>
              </div>
              <div className="plan-savings">
                <p>Save ₹{userPlans.yearly.savings} per year!</p>
                <p className="monthly-equivalent">
                  Just ₹{userPlans.yearly.monthlyEquivalent}/month
                </p>
              </div>
            </div>
            <div className="plan-features">
              <h4>Everything in Monthly, Plus:</h4>
              <ul>
                {userPlans.yearly.features.loanAccess && (
                  <li>
                    <span className="feature-icon">💰</span>
                    <span>Instant Loan Access</span>
                  </li>
                )}
                {userPlans.yearly.features.premiumSupport && (
                  <li>
                    <span className="feature-icon">🎧</span>
                    <span>24/7 Premium Support</span>
                  </li>
                )}
                {userPlans.yearly.features.priorityListing && (
                  <li>
                    <span className="feature-icon">⭐</span>
                    <span>Top Priority Listing</span>
                  </li>
                )}
                {userPlans.yearly.features.advancedAnalytics && (
                  <li>
                    <span className="feature-icon">📊</span>
                    <span>Advanced Analytics & Reports</span>
                  </li>
                )}
                {userPlans.yearly.features.unlimitedProducts && (
                  <li>
                    <span className="feature-icon">📦</span>
                    <span>Unlimited Products</span>
                  </li>
                )}
                {userPlans.yearly.features.reducedCommission && (
                  <li>
                    <span className="feature-icon">💸</span>
                    <span>Reduced Commission</span>
                  </li>
                )}
                {userPlans.yearly.features.freeDeliveries > 0 && (
                  <li>
                    <span className="feature-icon">🚚</span>
                    <span>{userPlans.yearly.features.freeDeliveries} Free Deliveries/year</span>
                  </li>
                )}
                {userPlans.yearly.features.higherEarnings && (
                  <li>
                    <span className="feature-icon">📈</span>
                    <span>15% Higher Earnings</span>
                  </li>
                )}
                <li>
                  <span className="feature-icon">🎁</span>
                  <span>2 Months Free</span>
                </li>
              </ul>
            </div>
            <button
              onClick={() => handleSubscribe('yearly')}
              className="btn-subscribe btn-subscribe-featured"
              disabled={currentSubscription?.planType === 'yearly'}
            >
              {currentSubscription?.planType === 'yearly' ? 'Current Plan' : 'Subscribe Now'}
            </button>
          </div>
        )}

        {/* Free Plan */}
        <div className={`pricing-card ${!isSubscribed ? 'current-plan' : ''}`}>
          <div className="plan-header">
            <h2>Free Plan</h2>
            <div className="plan-price">
              <span className="currency">₹</span>
              <span className="amount">0</span>
              <span className="period">/forever</span>
            </div>
          </div>
          <div className="plan-features">
            <h4>Basic Features:</h4>
            <ul>
              <li>
                <span className="feature-icon">❌</span>
                <span>No Loan Access</span>
              </li>
              <li>
                <span className="feature-icon">✅</span>
                <span>Basic Support</span>
              </li>
              <li>
                <span className="feature-icon">❌</span>
                <span>No Priority Listing</span>
              </li>
              <li>
                <span className="feature-icon">❌</span>
                <span>Limited Analytics</span>
              </li>
              <li>
                <span className="feature-icon">❌</span>
                <span>Limited Products (10 max)</span>
              </li>
              <li>
                <span className="feature-icon">❌</span>
                <span>Standard Commission (5%)</span>
              </li>
            </ul>
          </div>
          {!isSubscribed && (
            <button className="btn-subscribe btn-current" disabled>
              Current Plan
            </button>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="payment-modal-overlay" onClick={() => !processing && setShowPaymentModal(false)}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Complete Payment</h2>
            <div className="payment-summary">
              <p className="plan-name">{selectedPlan?.toUpperCase()} PLAN</p>
              <p className="amount">₹{userPlans[selectedPlan]?.price}</p>
            </div>
            <div className="payment-methods">
              <h3>Select Payment Method:</h3>
              <div className="payment-options">
                <label className="payment-option">
                  <input
                    type="radio"
                    name="payment"
                    value="online"
                    checked={paymentMethod === 'online'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <span>💳 Credit/Debit Card</span>
                </label>
                <label className="payment-option">
                  <input
                    type="radio"
                    name="payment"
                    value="upi"
                    checked={paymentMethod === 'upi'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <span>📱 UPI</span>
                </label>
                <label className="payment-option">
                  <input
                    type="radio"
                    name="payment"
                    value="netbanking"
                    checked={paymentMethod === 'netbanking'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <span>🏦 Net Banking</span>
                </label>
              </div>
            </div>
            <div className="payment-actions">
              <button
                onClick={processPayment}
                className="btn-pay"
                disabled={processing}
              >
                {processing ? 'Processing...' : `Pay ₹${userPlans[selectedPlan]?.price}`}
              </button>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="btn-cancel-modal"
                disabled={processing}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </Layout>
  );
};

export default Subscription;
